import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Ticket, ArrowLeft, Plus, Trash2, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { code: "", type: "days", duration_days: 30, max_redemptions: "", expires_at: "", active: true, cadence: "monthly", percent_off: "", amount_off: "" };

export default function AdminPromoCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/promo-codes").then(({ data }) => setCodes(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "owner") return <Navigate to="/home" replace />;

  const create = async () => {
    if (!form.code.trim()) { toast.error("Enter a code."); return; }
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code.trim(),
        type: form.type,
        duration_days: form.type === "days" ? Number(form.duration_days) || 30 : null,
        max_redemptions: form.max_redemptions === "" ? null : Number(form.max_redemptions),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
        cadence: form.type === "discount" ? form.cadence : null,
        percent_off: form.type === "discount" && form.percent_off !== "" ? Number(form.percent_off) : null,
        amount_off: form.type === "discount" && form.amount_off !== "" ? Number(form.amount_off) : null,
      });
      toast.success("Promo code created.");
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the code.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, { ...c, active: !c.active });
      load();
    } catch { toast.error("Couldn't update the code."); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/promo-codes/${id}`);
    load();
  };

  const fmt = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="admin-promo-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth" data-testid="admin-promo-back">
        <ArrowLeft className="h-4 w-4" /> Back to settings
      </Link>
      <div className="flex items-center gap-3">
        <Ticket className="text-[#C89B55]" size={22} />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Admin</p>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold">Promo codes</h1>
        </div>
      </div>

      <div className="bg-white border border-[#E5DED4] rounded-2xl p-6 space-y-4" data-testid="promo-create-form">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Plus className="h-4 w-4 text-[#C89B55]" /> New code</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-sm">
            <span className="text-[#59535B]">Code</span>
            <input data-testid="promo-form-code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="SISTER30" className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
          </label>
          <label className="text-sm">
            <span className="text-[#59535B]">Type</span>
            <select data-testid="promo-form-type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#C89B55]">
              <option value="days">Free for N days</option>
              <option value="lifetime">Lifetime free</option>
              <option value="discount">Discount (% or $ off)</option>
            </select>
          </label>
          {form.type === "days" && (
            <label className="text-sm">
              <span className="text-[#59535B]">Free days</span>
              <input data-testid="promo-form-days" type="number" min="1" value={form.duration_days} onChange={(e) => setForm((f) => ({ ...f, duration_days: e.target.value }))}
                className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
            </label>
          )}
          {form.type === "discount" && (
            <>
              <label className="text-sm">
                <span className="text-[#59535B]">Applies to plan</span>
                <select data-testid="promo-form-cadence" value={form.cadence} onChange={(e) => setForm((f) => ({ ...f, cadence: e.target.value }))}
                  className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#C89B55]">
                  <option value="monthly">Monthly ($14.99)</option>
                  <option value="annual">Annual ($99.99)</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="text-[#59535B]">Percent off (%)</span>
                <input data-testid="promo-form-percent" type="number" min="1" max="99" value={form.percent_off}
                  onChange={(e) => setForm((f) => ({ ...f, percent_off: e.target.value, amount_off: "" }))}
                  placeholder="e.g. 20" className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
              </label>
              <label className="text-sm">
                <span className="text-[#59535B]">…or amount off ($)</span>
                <input data-testid="promo-form-amount" type="number" min="1" step="0.01" value={form.amount_off}
                  onChange={(e) => setForm((f) => ({ ...f, amount_off: e.target.value, percent_off: "" }))}
                  placeholder="e.g. 5" className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
              </label>
            </>
          )}
          <label className="text-sm">
            <span className="text-[#59535B]">Max redemptions (blank = unlimited)</span>
            <input data-testid="promo-form-max" type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
              placeholder="Unlimited" className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
          </label>
          <label className="text-sm">
            <span className="text-[#59535B]">Expiry date (optional)</span>
            <input data-testid="promo-form-expiry" type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
              className="mt-1 w-full border border-[#E5DED4] rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
          </label>
          <label className="text-sm flex items-center gap-2 mt-6">
            <input data-testid="promo-form-active" type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
            <span className="text-[#59535B]">Active</span>
          </label>
        </div>
        <button data-testid="promo-create-submit" onClick={create} disabled={saving}
          className="rounded-full bg-[#291B2D] text-[#FCFAF6] px-8 py-3 font-medium hover:bg-[#352238] disabled:opacity-40 transition-all flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create code
        </button>
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold mb-3">Existing codes</h2>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#8A838E]"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
        ) : codes.length === 0 ? (
          <div className="bg-[#F7F2EA] border border-[#E5DED4] rounded-2xl p-10 text-center text-[#59535B]">No promo codes yet.</div>
        ) : (
          <div className="space-y-3">
            {codes.map((c) => (
              <div key={c.id} data-testid={`promo-row-${c.id}`} className="bg-white border border-[#E5DED4] rounded-2xl p-5 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <p className="font-mono font-semibold text-lg tracking-wide">{c.code}</p>
                  <p className="text-sm text-[#59535B] mt-0.5">
                    {c.type === "lifetime"
                      ? "Lifetime free"
                      : c.type === "discount"
                        ? `${c.percent_off ? `${c.percent_off}% off` : `$${c.amount_off} off`} ${c.cadence} → $${c.discounted_value}`
                        : `${c.duration_days} days free`}
                    {" · "}{c.redemption_count}{c.max_redemptions ? `/${c.max_redemptions}` : ""} redeemed
                    {" · expires "}{fmt(c.expires_at)}
                  </p>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${c.active ? "bg-[#ECF3EE] text-[#3D6B52]" : "bg-[#F3E7E7] text-[#9E3A3A]"}`}>
                  {c.active ? "Active" : "Disabled"}
                </span>
                <button data-testid={`promo-toggle-${c.id}`} onClick={() => toggleActive(c)} aria-label="Toggle active"
                  className="p-2 rounded-full text-[#59535B] hover:bg-[#F7F2EA]"><Power className="h-4 w-4" /></button>
                <button data-testid={`promo-delete-${c.id}`} onClick={() => remove(c.id)} aria-label="Delete code"
                  className="p-2 rounded-full text-[#8A838E] hover:text-[#9E3A3A] hover:bg-[#FAF0F0]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
